import React, { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { isEmpty } from 'lodash';

import { closeContactSidebar } from 'store/v1/ui/ui.actions.js';
import { unarchiveContact } from 'store/v1/contacts/contacts.actions.js';
import { myServices } from 'v5/services/services.connected.app';

import SidebarDraw from 'v1/components/frame/SidebarDraw/SidebarDraw';
import ResourcePane from 'v1/components/feature/ResourceComponents/ResourcePane/ResourcePane';
import { Loading } from 'v1/components/shared';
import { ArchivedBanner } from '~/v5/patterns/widgets/ArchivedBanner.js';
import { UnsavedChangesProtection } from 'v5/patterns/forms/unsaved-changes/UnsavedChangesProtection';
import { useHoldResourceDrawer } from 'v5/features/resources/resourceDrawerGuard';
import { createSamePathnameMatcher } from 'v5/platform/react-router';

const ResourceSidebarDrawer = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { showContactSidebar, selectedContact, selectedContactAppScope } =
    useSelector(state => state.ui);

  const closeSidebar = () => dispatch(closeContactSidebar());
  // PrivateRoute closes the drawer on every pathname change; query-only
  // navigation keeps it open, with any unsaved edits inside.
  const allowQueryNavigation = useMemo(
    () => createSamePathnameMatcher(location.pathname),
    [location.pathname]
  );

  const handleUnarchive = () => {
    dispatch(unarchiveContact(selectedContact.id))
      .then(() => {
        myServices.toastService.open({
          type: 'success',
          message: 'Resource unarchived'
        });
      })
      .catch(error => {
        myServices.toastService.open({
          type: 'error',
          message: 'Failed to unarchive the resource.'
        });
        console.error('Failed to unarchive the resource.', error);
      });
  };

  // Closing the drawer, navigating away or opening another resource in it asks
  // first while the resource form inside holds unsaved changes.
  return (
    <UnsavedChangesProtection
      open={!!showContactSidebar}
      onOpenChange={open => {
        if (!open) closeSidebar();
      }}
      allowNavigation={allowQueryNavigation}
    >
      {({ onOpenChange, requestLeave }) => (
        <SidebarDraw
          isOpen={showContactSidebar}
          closeSidebar={() => onOpenChange(false)}
        >
          <HoldResourceDrawer
            resourceId={selectedContact?.id}
            requestLeave={requestLeave}
          />
          <div className="SidebarDraw-flex-container">
            {selectedContact?.archived && (
              <ArchivedBanner
                onUnarchive={handleUnarchive}
                assetName="resource"
              />
            )}
            <div className="SidebarDraw-flex-content">
              {!isEmpty(selectedContact) ? (
                <ResourcePane
                  resource={selectedContact}
                  appScope={selectedContactAppScope ?? undefined}
                  detailsPadded
                />
              ) : (
                <Loading />
              )}
            </div>
          </div>
        </SidebarDraw>
      )}
    </UnsavedChangesProtection>
  );
};

/** Mounted while the drawer is open: see `resourceDrawerGuard`. */
const HoldResourceDrawer = ({ resourceId, requestLeave }) => {
  useHoldResourceDrawer(resourceId, requestLeave);
  return null;
};

ResourceSidebarDrawer.propTypes = {};

export default ResourceSidebarDrawer;
